// src/components/BirthDetailsForm.js

import React, { useEffect, useState } from "react";
import { normalizeDateToISO, normalizeTimeTo24h } from "../lib/formatters";
import { GooglePlaceInput } from "./GooglePlaceInput";
import { LatLonInputs } from "./LatLonInputs";

export function BirthDetailsForm(props) {
  const {
    value,
    onChange,
    locationMode,
    onLocationModeChange,
    locationErrors,
  } = props;

  const [dateRaw, setDateRaw] = useState("");
  const [timeRaw, setTimeRaw] = useState("");
  const [dateError, setDateError] = useState("");
  const [timeError, setTimeError] = useState("");

  // Initialize / sync from existing value
    useEffect(() => {
      if (value && value.date) {
        const parts = value.date.split("-");
        if (parts.length === 3) {
          const [y, m, d] = parts;
          setDateRaw(`${d}-${m}-${y}`); // DD-MM-YYYY display
        }
      } else {
        setDateRaw("");
      }

      if (value && value.time) {
        setTimeRaw(value.time);
      } else {
        setTimeRaw("");
      }
    }, [value]);


  useEffect(() => {
    if (!value.date) {
      setDateRaw("");
      setDateError("");
    }
  }, [value.date]);

  useEffect(() => {
    if (!value.time) {
      setTimeRaw("");
      setTimeError("");
    }
  }, [value.time]);


  const update = (patch) => {
    onChange({ ...value, ...patch });
  };

  const handleDateBlur = () => {
    if (!dateRaw.trim()) {
      update({ date: "" });
      setDateError("");
      return;
    }

    const iso = normalizeDateToISO(dateRaw);
    if (iso) {
      update({ date: iso });
      const [y, m, d] = iso.split("-");
      setDateRaw(`${d}-${m}-${y}`);
      setDateError("");
    } else {
      update({ date: "" });
      setDateError("Invalid date. Use DD-MM-YYYY.");
    }
  };

  const handleTimeBlur = () => {
    if (!timeRaw.trim()) {
      update({ time: "" });
      setTimeError("");
      return;
    }

    const t = normalizeTimeTo24h(timeRaw);
    if (t) {
      update({ time: t });
      setTimeRaw(t);
      setTimeError("");
    } else {
      update({ time: "" });
      setTimeError("Invalid time. Example: 8:25 pm or 20:25.");
    }
  };

  const baseInputStyle = {
    width: "100%",
    padding: "5px 7px",
    marginTop: 2,
    borderRadius: 6,
    border: "1px solid #4b5563",
    background: "#020617",
    color: "#e5e7eb",
    fontSize: 13,
    boxSizing: "border-box",
  };

  const labelStyle = { fontSize: 13, display: "block", marginBottom: 6 };
  const helperStyle = { fontSize: 11, color: "#9ca3af", marginTop: 2 };

  const disabledInputStyle = {
    ...baseInputStyle,
    background: "#020617",
    color: "#6b7280",
    cursor: "not-allowed",
  };

  const isCityMode = locationMode === "city";
  const isCoordsMode = locationMode === "coords";

  return (
    <div style={{ marginTop: 6 }}>
      {/* Date */}
      <div style={{ marginBottom: 6 }}>
        <label style={labelStyle}>
          Date of Birth:
          <input
            id="birth-date"
            placeholder="DD-MM-YYYY"
            value={dateRaw}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
              let formatted = digits;
              if (digits.length > 4)
                formatted = digits.slice(0, 2) + "-" + digits.slice(2, 4) + "-" + digits.slice(4);
              else if (digits.length > 2)
                formatted = digits.slice(0, 2) + "-" + digits.slice(2);
              setDateRaw(formatted);
            }}
            onBlur={handleDateBlur}
            style={baseInputStyle}
            inputMode="numeric"
          />
        </label>
        <div style={helperStyle}>
          Example: 07-02-1991, 07021991
        </div>
        {dateError && (
          <div style={{ fontSize: 11, color: "#f97373", marginTop: 2 }}>
            {dateError}
          </div>
        )}
      </div>

      {/* Time */}
      <div style={{ marginBottom: 6 }}>
        <label style={labelStyle}>
          Time of Birth:
          <input
            id="birth-time"
            placeholder="HH:MM (24h or 12h)"
            value={timeRaw}
            onChange={(e) => setTimeRaw(e.target.value)}
            onBlur={handleTimeBlur}
            style={baseInputStyle}
          />
        </label>
        <div style={helperStyle}>
          Examples: 8:25 pm, 20:25, 0825
        </div>
        {timeError && (
          <div style={{ fontSize: 11, color: "#f97373", marginTop: 2 }}>
            {timeError}
          </div>
        )}
      </div>

      {/* Location mode toggle */}
      <div style={{ marginBottom: 4, marginTop: 8, fontSize: 12 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={isCoordsMode}
            onChange={(e) =>
              onLocationModeChange(e.target.checked ? "coords" : "city")
            }
          />
          <span>Enter latitude & longitude manually (advanced)</span>
        </label>
      </div>

      {/* Place / City input (active only in city mode) */}
      <GooglePlaceInput
        value={value.placeName}
        onChange={(placeName) => update({ placeName })}
        disabled={isCoordsMode}
        error={locationErrors.placeName}
      />

      {/* Lat / Lon inputs */}
      <LatLonInputs
        lat={value.lat || ""}   // make sure we always pass a string
        lon={value.lon || ""}   // same here
        onChange={(lat, lon) => update({ lat, lon })}
        disabled={isCityMode}
        errors={{ lat: locationErrors.lat, lon: locationErrors.lon }}
        baseInputStyle={baseInputStyle}
        disabledInputStyle={disabledInputStyle}
      />
    </div>
  );
}
